import { take } from 'rxjs';
import { AuthActions } from '@core/store/auth/auth.actions';
import { AsyncPipe, CommonModule, DOCUMENT, Location } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { LayoutVerticalComponent } from '@core/shell/layouts/layout-vertical/layout-vertical.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Store } from '@ngrx/store';
import { selectAuth } from '@core/store/auth/auth.selector';
import { selectGlobal } from '@core/store/global/global.selector';
import { SpinnerComponent } from '@core/shell/spinner/spinner.component';
import { isTokenValid } from '@core/utils/auth';
import { TranslateService } from '@ngx-translate/core';
import { ChangeDetectorRef } from '@angular/core';
import { filter } from 'rxjs';
import { environment } from '@env/environment';
import { isExternalPricingRoute } from '@core/utils/external-pricing-session';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    LayoutVerticalComponent,
    RouterOutlet,
    MatProgressSpinnerModule,
    AsyncPipe,
    SpinnerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'web-build-angular';
  isAuthentication: boolean = false;
  currentPath: string = '';
  isLoading: boolean = false;
  isPreLoad: boolean = true;

  constructor(
    private store: Store,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private location: Location,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.store.select(selectAuth).subscribe((value) => {
      this.isAuthentication = value.credential ? isTokenValid(value.credential) : false;
    });

    // Resolve from the browser URL immediately so /pricing/... (incl. success return)
    // never briefly shows the admin shell while a canActivate guard is still running.
    this.currentPath = this.normalizePath(this.location.path() || this.router.url);

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationEnd =>
            event instanceof NavigationStart || event instanceof NavigationEnd
        )
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.currentPath = this.normalizePath(event.url);
        } else {
          this.currentPath = this.normalizePath(event.urlAfterRedirects || event.url);
        }
      });
  }

  ngOnInit(): void {
    this.store.select(selectAuth).pipe(take(1)).subscribe((auth) => {
      if (auth.credential && isTokenValid(auth.credential)) {
        this.store.dispatch(AuthActions.loadPermissions());
      }
    });

    this.store.select(selectGlobal).subscribe((value) => {
      this.isLoading = value.isLoading;
      this.cdr.detectChanges();
    });
    this.addUserWayScript();
  }

  get shouldShowLayout(): boolean {
    const pathsWithoutLayout = ['/login', '/pricing'];
    const isPricingRoute = this.currentPath === '/pricing' || this.currentPath.startsWith('/pricing/');
    const isPokResultRoute = this.currentPath.startsWith('/payment/pok');
    const isPaymentPendingRoute = this.currentPath.startsWith('/payment/pending');
    const hideLayout =
      pathsWithoutLayout.includes(this.currentPath) ||
      isPricingRoute ||
      isPokResultRoute ||
      isPaymentPendingRoute;
    return this.isAuthentication && !hideLayout;
  }

  private normalizePath(url: string): string {
    return (url ?? '').split('?')[0].split('#')[0];
  }

  private addUserWayScript(): void {
    const s = this.document.createElement('script');
    /* uncomment the following line to override default position*/
    /* s.setAttribute("data-position", 3);*/
    /* uncomment the following line to override default size (values: small, large)*/
    /* s.setAttribute("data-size", "small");*/
    /* uncomment the following line to override default language (e.g., fr, de, es, he, nl, etc.)*/
    /* s.setAttribute("data-language", "language");*/
    /* uncomment the following line to override color set via widget (e.g., #053f67)*/
    /* s.setAttribute("data-color", "#053e67");*/
    /* uncomment the following line to override type set via widget (1=person, 2=chair, 3=eye, 4=text)*/
    /* s.setAttribute("data-type", "1");*/
    /* s.setAttribute("data-statement_text:", "Our Accessibility Statement");*/
    /* s.setAttribute("data-statement_url", "http://www.example.com/accessibility")";*/
    /* uncomment the following line to override support on mobile devices*/
    s.setAttribute('data-mobile', 'true');
    s.setAttribute('data-account', environment.userWayId);
    /* uncomment the following line to set custom trigger action for accessibility menu*/
    /* s.setAttribute("data-trigger", "triggerId")*/
    s.src = 'https://cdn.userway.org/widget.js';
    s.defer = true;
    (this.document.body || this.document.head ).appendChild(s);
  }
}
